<script setup lang="ts">
import { whenever } from '@vueuse/core'
import { ClustersService } from 'frontend/clusters/clusters-service'
import { clusterGeoFenceStyle } from 'frontend/clusters/constants/geo-fence-styles'
import { getMapZoneGeoFenceStyle } from 'frontend/map-zones/constants/geo-fence-styles'
import { MapZonesService } from 'frontend/map-zones/map-zones-service'
import { stagingAreaGeoFenceStyle } from 'frontend/staging-areas/constants/geo-fence-styles'
import { StagingAreasService } from 'frontend/staging-areas/staging-areas-service'
import { useTheme } from 'frontend/themes/composables/useTheme'
import { type Cluster } from 'types/models/cluster'
import { type MapZone } from 'types/models/map-zone'
import { type StagingArea } from 'types/models/staging-area'
import { computed, ref } from 'vue'

import FifaGisPolygon from '../map-items/fifa-gis-polygon.vue'

interface Props {
  clustersLayerEnabled?: boolean
  mapZonesLayerEnabled?: boolean
  stagingAreasLayerEnabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  clustersLayerEnabled: true,
})

const { currentThemeType } = useTheme()

const clusterService = new ClustersService()
const stagingAreasService = new StagingAreasService()
const mapZonesService = new MapZonesService()

const clusterGeoFencesLayerEnabled = ref(props.clustersLayerEnabled)
const stagingAreaGeoFencesLayerEnabled = ref(props.mapZonesLayerEnabled)
const mapZoneGeoFencesLayerEnabled = ref(props.stagingAreasLayerEnabled)

const clusters = ref<Array<Cluster>>()
const mapZones = ref<Array<MapZone>>()
const stagingAreas = ref<Array<StagingArea>>()

const mapZoneGeoFenceStyle = computed(() => {
  return getMapZoneGeoFenceStyle(currentThemeType.value)
})

whenever(
  clusterGeoFencesLayerEnabled,
  () => {
    if (!clusters.value) {
      fetchClusters()
    }
  },
  {
    immediate: true,
  },
)

whenever(
  stagingAreaGeoFencesLayerEnabled,
  () => {
    if (!stagingAreas.value) {
      fetchStagingAreas()
    }
  },
  {
    immediate: true,
  },
)

whenever(
  mapZoneGeoFencesLayerEnabled,
  () => {
    if (!mapZones.value) {
      fetchMapZones()
    }
  },
  {
    immediate: true,
  },
)

async function fetchClusters() {
  clusters.value = (
    await clusterService.index({
      data: {
        fields: ['id', 'name', 'geo_fence'],
        normalized: false,
        geo_fence_not_null: true,
      },
    })
  ).contents
}

async function fetchStagingAreas() {
  stagingAreas.value = (
    await stagingAreasService.index({
      data: {
        fields: ['id', 'name', 'geo_fence'],
        normalized: false,
        geo_fence_not_null: true,
      },
    })
  ).contents
}

async function fetchMapZones() {
  mapZones.value = (
    await mapZonesService.index({
      data: {
        fields: ['id', 'name', 'geo_fence'],
        normalized: false,
        geo_fence_not_null: true,
      },
    })
  ).contents
}

const showLayersPanel = ref(false)
</script>

<template lang="pug">
.fifa-gis-layer-visibility__control.position-absolute
  .position-relative
    a.rounded-circle.shadow.btn.btn-light.p-2(@click.stop="showLayersPanel = !showLayersPanel")
      i.fs-5.mi-outlined.mi-layers

    transition(name="fade-right")
      .fifa-gis-layer-visibility-panel.bg-surface.position-absolute(v-if="showLayersPanel")
        .fifa-gis-layer-visibility-panel__header
          h5.fifa-gis-layer-visibility-panel__title
            | Layers
        .fifa-gis-layer-visibility-panel__body
        ea-checkbox(
          v-model="clusterGeoFencesLayerEnabled"
          label="Clusters"
          name="clusterGeoFencesLayerEnabled"
        )
        ea-checkbox(
          v-model="stagingAreaGeoFencesLayerEnabled"
          label="Staging Areas"
          name="stagingAreaGeoFencesLayerEnabled"
        )
        ea-checkbox(
          v-model="mapZoneGeoFencesLayerEnabled"
          label="Map Zones"
          name="mapZoneGeoFencesLayerEnabled"
        )

template(v-if="clusterGeoFencesLayerEnabled")
  fifa-gis-polygon(
    v-for="(geoFencable, index) in clusters"
    :editable="false"
    :focus-on-show="false"
    :key="geoFencable.id || index"
    :polygon="geoFencable.geoFence"
    :polygonStyle="clusterGeoFenceStyle"
  )

template(v-if="mapZoneGeoFencesLayerEnabled")
  fifa-gis-polygon(
    v-for="(geoFencable, index) in mapZones"
    :editable="false"
    :focus-on-show="false"
    :key="geoFencable.id || index"
    :polygon="geoFencable.geoFence"
    :polygonStyle="mapZoneGeoFenceStyle"
  )

template(v-if="stagingAreaGeoFencesLayerEnabled")
  fifa-gis-polygon(
    v-for="(geoFencable, index) in stagingAreas"
    :editable="false"
    :focus-on-show="false"
    :key="geoFencable.id || index"
    :polygon="geoFencable.geoFence"
    :polygonStyle="stagingAreaGeoFenceStyle"
  )
</template>

<style>
.fifa-gis-layer-visibility__control {
  z-index: 1000;
  top: 24px;
  left: 24px;
}

.fifa-gis-layer-visibility-panel {
  padding: 12px;
  border-radius: 4px;
  left: calc(100% + 12px);
  top: 0px;
  font-size: 13px;
  width: 140px;
}
</style>
